import { useState, useEffect } from "react";
import { Outlet, useNavigate, useParams, useSearchParams } from "react-router-dom";
import "./ReorderLayout.css"
import ServerError from "../../components/ServerError/ServerError";
import LoadingModal from "../../components/Modals/LoadingModal/LoadingModal";
import HeaderController from "../../components/Headers/HeaderController";
import * as http from "../../utils/http";
import SubscriptionModal from "../../components/Modals/SubscriptionModal/SubscriptionModal";
import StickyNotification from "../../components/StickyNotification/StickyNotification";
import { getReorderNavigationParams, buildReorderApiQuery } from "./ReorderTrackingManager";
import { ReorderTrackingContext, WlReorderSubscriptionOffer } from "./reorderTypes";
import { assignWlReorderVariant, WlReorderVariant } from "../../utils/wlReorderSubscriptionExperiment";
import CarePlusStepperHeader from "../CarePlusShared/CarePlusStepperHeader";
import * as translator from "../../utils/translator";


type ReorderData = {
    rawQuestionnaire: string,
    prefilledQuestionnaire: any,
    catalogMention:string,
    orderProductItems: any,
    isOnlyPrescription : boolean,
    currencyRate: number,
    wlSubscriptionOffer: WlReorderSubscriptionOffer | null,
    trackingContext: ReorderTrackingContext | null,
    isCarePlus: boolean
}

// Care+ progress bar step per reorder step; Shipping shows "checkout".
const CARE_PLUS_STEP_BY_HEADER_STEP: Record<string, "treatment" | "plan"> = {
    Questionnaire: "treatment",
    Treatment: "plan",
};

/** The Care+ progress bar step, or null to keep the funnel header (non Care+ categories). */
export const getReorderCarePlusStep = (isCarePlus: boolean | undefined, headerStep: string) =>
    isCarePlus ? CARE_PLUS_STEP_BY_HEADER_STEP[headerStep] ?? null : null;

const ReorderLayout = () => {
    const { country, language, order_hash_id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<number | null>(null);
    const [reorderData, setReorderData] = useState<ReorderData | null>(null);
    // null = not in the WL reorder test.
    const [wlReorderVariant, setWlReorderVariant] = useState<WlReorderVariant | null>(null);
    // The WL offer screen has its own background.
    const [isWlReorderPage, setWlReorderPage] = useState<boolean>(false);
    const [showSubscriptionModal,setShowSubscriptionModal] = useState<boolean>(false);
    const [headerStep,setHeaderStep,] = useState<string>("");
    const [stickyNotificationDetails, setStickyNotificationDetails] = useState({
        show: false,
        title: "",
        content: "",
        level: "",
        showInfoIcon: true,
        refresh: false,
    });

    const reorderApiQuery = buildReorderApiQuery(searchParams);
    useEffect(() => {
        http
            .getFromServer(`reorder/${order_hash_id}/${country}/${language}${reorderApiQuery}`, [http.MIDDLEWARES.CUSTOMER_ID])
            .then((response) => {
                if (response.data.redirectToChooseTreatment) {
                    const navQuery = new URLSearchParams(getReorderNavigationParams()).toString();
                    navigate(`/select_treatment/${country}/${language}/${response.data.catalogMention}?${navQuery}`);
                }
                // Fallback bucketing for devices that arrive without an arm (email link, CMS widget); sticky otherwise.
                if (response.data.wlSubscriptionOffer) {
                    setWlReorderVariant(assignWlReorderVariant(response.data.wlSubscriptionOffer.testWeights));
                }
                setReorderData(response.data);
            })
            .catch((error) => {
                if(error.response.status === 400){
                    window.clientLogger.warn(`Unauthorize try to access reorder of order: ${order_hash_id}`)
                    setError(400);
                } else if(error.response.status === 404){
                    setError(400);
                } else {
                    window.clientLogger.error("error getFromServer MedicalDisclaimerPage: ", error)
                    setError(500);
                }
            })
            .finally(() => {
                setLoading(false);
            })

    }, []);

    const getMaxDiscountOfSubscriptionPlans = () => {
        const productItemSubscriptionPlans = reorderData!.orderProductItems[0].subscriptionPlans[reorderData!.orderProductItems[0].product_item_id];
        let maxDiscount = 0;
        productItemSubscriptionPlans.forEach((subscriptionPlan:any) => {
            if (subscriptionPlan?.discount > maxDiscount){
                maxDiscount = subscriptionPlan.discount;
            }
        })

        return maxDiscount;
    }


    const carePlusStep = getReorderCarePlusStep(reorderData?.isCarePlus, headerStep);

    //Loading block
    if (loading) {
        return <LoadingModal />;

    //Error Block
    } else if (error) {
        return (
            <>
                <HeaderController showSteps={false} stage={"Questionnaire"} />
                <ServerError status={error} />
            </>
        );

    //Component Block
    } else {
        return (
            <>
                {
                    showSubscriptionModal ? 
                    <SubscriptionModal 
                    showModalCallback={(show:boolean)=> setShowSubscriptionModal(show)}
                    language={language}
                    country={country!}
                    discount={getMaxDiscountOfSubscriptionPlans()}
                    ftd={99}
                    />
                    :
                    null
                }
               
                <div className="page-wrapper">

                    <StickyNotification
                        showNotification={stickyNotificationDetails.show}
                        title={stickyNotificationDetails.title}
                        onClick={() => setStickyNotificationDetails((currentDetails:any) => { return {...currentDetails, show:false}})}  
                        content={stickyNotificationDetails.content}
                        level={stickyNotificationDetails.level}
                        showInfoIcon={stickyNotificationDetails.showInfoIcon}
                        refresh={stickyNotificationDetails.refresh}
                    />

                    <HeaderController language={language} stage={headerStep} showSteps={!carePlusStep} />
                    {
                        reorderData &&
                        <main className="page-content-container" style={isWlReorderPage ? { backgroundColor: "#f7f8f6", padding: "32px 16px" } : undefined}>
                            {carePlusStep && (
                                <CarePlusStepperHeader
                                    current={carePlusStep}
                                    t={(key, fallback) => translator.translate(language, "choose_plan_care_plus", key) || fallback}
                                    onBack={() => navigate(-1)}
                                />
                            )}
                            <Outlet context={{
                                reorderData,
                                country,
                                language,
                                orderHashID: order_hash_id,
                                setLoading,
                                setStickyNotificationDetails,
                                setShowSubscriptionModal,
                                getMaxDiscountOfSubscriptionPlans,
                                setHeaderStep,
                                wlReorderVariant,
                                setWlReorderPage,
                            }}
                            />
                        </main>
                    }
                </div>
            </>
        );
    }
};

export default ReorderLayout;
